/* Quản lý công việc: trạng thái, quyền theo vai trò, tiến độ, hạn, lọc/sắp xếp. Port từ app cũ. */
import { today as todayFn } from './dates';
import { ouKey } from './orgchart';

export type TaskStatus = 'todo' | 'doing' | 'review' | 'done' | 'cancel';
export type TaskPriority = 'urgent' | 'high' | 'normal' | 'low';
export type TaskRepeat = '' | 'week' | 'month' | 'quarter' | 'year';

/** [mã, tên, màu] */
export const TK_ST: [TaskStatus, string, string][] = [['todo', 'Cần làm', 'var(--muted)'], ['doing', 'Đang làm', 'var(--accent)'], ['review', 'Chờ duyệt', 'var(--warn)'], ['done', 'Hoàn thành', 'var(--ok)'], ['cancel', 'Tạm dừng / Hủy', 'var(--danger)']];
export const TK_PRI: [TaskPriority, string][] = [['urgent', 'Khẩn cấp'], ['high', 'Cao'], ['normal', 'Bình thường'], ['low', 'Thấp']];
export const TK_REPEAT: Record<string, string> = { week: 'Hằng tuần', month: 'Hằng tháng', quarter: 'Hằng quý', year: 'Hằng năm' };

export const tkStName = (k: string | undefined): string => (TK_ST.find(x => x[0] === k) || TK_ST[0])[1];
export const tkPriName = (k: string | undefined): string => (TK_PRI.find(x => x[0] === k) || TK_PRI[2])[1];

export interface TaskCheck { t: string; done: boolean }
/** Công việc theo tên trường của app cũ (giao diện ánh xạ từ cột CSDL). */
export interface TaskLite {
  id: string; title: string; status: string; priority?: string; progress?: number | string; due?: string; start?: string; group?: string; desc?: string; dept?: string;
  assigneeId?: string; createdById?: string; watcherIds?: string[]; checks?: TaskCheck[]; createdAt?: string; doneAt?: string; repeat?: string;
}
/** Người đang đăng nhập (null = chưa xác định → cho phép, như app cũ). */
export interface TaskUser { id: string; role?: string }

const isAdmin = (me: TaskUser | null): boolean => me?.role === 'admin';
export const tkWatch = (t: Pick<TaskLite, 'watcherIds'>): string[] => t.watcherIds || [];
const DONE = ['done', 'cancel'];

export const tkLate = (t: Pick<TaskLite, 'due' | 'status'>, todayIso: string = todayFn()): boolean => !!t.due && t.due < todayIso && !DONE.includes(t.status);
/** Sắp đến hạn: chưa quá hạn và còn không quá 3 ngày. */
export function tkSoon(t: Pick<TaskLite, 'due' | 'status'>, todayIso: string = todayFn()): boolean {
  if (!t.due || DONE.includes(t.status) || tkLate(t, todayIso)) return false;
  return (+new Date(t.due) - +new Date(todayIso)) / 864e5 <= 3;
}

export const tkInvolved = (t: TaskLite, me: TaskUser | null): boolean => !me || isAdmin(me) || t.assigneeId === me.id || t.createdById === me.id || tkWatch(t).includes(me.id);
export const tkCanEdit = (t: TaskLite, me: TaskUser | null): boolean => !me || isAdmin(me) || t.createdById === me.id;
export const tkCanWork = (t: TaskLite, me: TaskUser | null): boolean => tkCanEdit(t, me) || t.assigneeId === me?.id;

/** Tiến độ: hoàn thành = 100%; có việc con thì tính theo việc con đã xong; không thì theo ô tiến độ. */
export function tkProg(t: Pick<TaskLite, 'checks' | 'status' | 'progress'>): number {
  const c = t.checks || [];
  return t.status === 'done' ? 100 : c.length ? Math.round(c.filter(x => x.done).length / c.length * 100) : +(t.progress ?? 0) || 0;
}

/** Các bước chuyển trạng thái được phép theo vai trò: [trạng thái mới, nhãn nút]. */
export function tkMoves(t: TaskLite, me: TaskUser | null): [TaskStatus, string][] {
  const st = t.status || 'todo', boss = tkCanEdit(t, me), mv: [TaskStatus, string][] = [];
  if (st === 'todo') mv.push(['doing', 'Bắt đầu làm']);
  if (st === 'doing') { mv.push(['review', 'Gửi duyệt']); mv.push(['done', 'Hoàn thành']); }
  if (st === 'review' && boss) { mv.push(['done', 'Duyệt – Hoàn thành']); mv.push(['doing', 'Trả lại làm tiếp']); }
  if (st === 'done' || st === 'cancel') mv.push(['doing', 'Mở lại']);
  if (!DONE.includes(st) && boss) mv.push(['cancel', 'Tạm dừng / Hủy']);
  return mv;
}

/** Ngày của kỳ kế tiếp của việc định kỳ. */
export function tkShift(iso: string | undefined, rep: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (rep === 'week') d.setDate(d.getDate() + 7); else d.setMonth(d.getMonth() + (({ month: 1, quarter: 3, year: 12 } as Record<string, number>)[rep || ''] || 0));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Nội dung thông báo + nhật ký khi đổi trạng thái: [loại thông báo, câu mô tả]. */
export const TK_MSG: Record<string, [string, string]> = {
  doing: ['status', 'đã bắt đầu làm'], review: ['review', 'gửi duyệt kết quả'], done: ['done', 'đã hoàn thành'], cancel: ['cancel', 'tạm dừng / hủy công việc'], todo: ['status', 'chuyển về Cần làm'],
};

export interface TaskFilter { scope: string; who: string; dept: string; pri: string; q: string; /** id tag của tôi; '-' = chưa gắn tag nào; '' = tất cả */ tag?: string }

/* ---------- tag cá nhân: mỗi người tự đặt tên, chọn màu và gắn cho công việc; chỉ người đó nhìn thấy ---------- */
export interface UserTag { id: string; name: string; color: string }
export interface TagDoc { tags: UserTag[]; map: Record<string, string[]> }
export const TG_COLORS = ['#d93f3f', '#e8710a', '#c99a06', '#2e9d4f', '#0f9d9a', '#1a73e8', '#5b5fc7', '#9334e6', '#d01884', '#795548', '#5f6b7a', '#0b5394'];
export const NO_TAGS: TagDoc = { tags: [], map: {} };
/** Chuẩn hoá bản ghi lấy từ CSDL (có thể thiếu / sai kiểu). */
export function tagDoc(raw: { tags?: unknown; map?: unknown } | null | undefined): TagDoc {
  return { tags: Array.isArray(raw?.tags) ? (raw!.tags as UserTag[]) : [], map: raw?.map && typeof raw.map === 'object' && !Array.isArray(raw.map) ? (raw.map as Record<string, string[]>) : {} };
}
export const tgById = (d: TagDoc, id: string): UserTag | undefined => d.tags.find(x => x.id === id);
/** Các tag (còn tồn tại) đang gắn cho công việc. */
export const tgOf = (d: TagDoc, taskId: string): string[] => (d.map[taskId] || []).filter(id => tgById(d, id));
export const tgNameTaken = (d: TagDoc, name: string, exceptId?: string): boolean => d.tags.some(g => g.id !== exceptId && g.name.toLowerCase() === name.toLowerCase());
/** Thêm tag mới (và gắn luôn vào công việc `taskId` nếu có). */
export function tgAdd(d: TagDoc, tag: UserTag, taskId?: string | null): TagDoc {
  const map = { ...d.map };
  if (taskId) map[taskId] = [...(map[taskId] || []), tag.id];
  return { tags: [...d.tags, tag], map };
}
export const tgSetColor = (d: TagDoc, id: string, color: string): TagDoc => ({ tags: d.tags.map(g => (g.id === id ? { ...g, color } : g)), map: d.map });
export const tgRename = (d: TagDoc, id: string, name: string): TagDoc => ({ tags: d.tags.map(g => (g.id === id ? { ...g, name } : g)), map: d.map });
/** Xoá tag khỏi danh sách và khỏi mọi công việc. */
export function tgRemove(d: TagDoc, id: string): TagDoc {
  const map: Record<string, string[]> = {};
  for (const [k, l] of Object.entries(d.map)) { const r = l.filter(x => x !== id); if (r.length) map[k] = r; }
  return { tags: d.tags.filter(x => x.id !== id), map };
}
/** Bật / tắt một tag cho công việc. */
export function tgToggle(d: TagDoc, taskId: string, tagId: string, on: boolean): TagDoc {
  const cur = new Set(d.map[taskId] || []);
  if (on) cur.add(tagId); else cur.delete(tagId);
  const map = { ...d.map };
  if (cur.size) map[taskId] = [...cur]; else delete map[taskId];
  return { tags: d.tags, map };
}
/** Lọc theo phạm vi / người / bộ phận / ưu tiên / từ khoá và sắp xếp: ưu tiên → hạn → ngày tạo. `assigneeName` trả về tên người được giao. */
export function tkFiltered<T extends TaskLite>(all: T[], f: TaskFilter, me: TaskUser | null, assigneeName: (t: T) => string, todayIso: string = todayFn(), tg: TagDoc = NO_TAGS): T[] {
  const q = ouKey(f.q);
  return all.filter(t => tkInvolved(t, me))
    .filter(t => (f.scope !== 'mine' || t.assigneeId === me?.id) && (f.scope !== 'given' || t.createdById === me?.id) && (f.scope !== 'watch' || tkWatch(t).includes(me?.id as string)) && (f.scope !== 'late' || tkLate(t, todayIso))
      && (!f.who || t.assigneeId === f.who) && (!f.dept || t.dept === f.dept) && (!f.pri || (t.priority || 'normal') === f.pri)
      && (!f.tag || (f.tag === '-' ? !tgOf(tg, t.id).length : tgOf(tg, t.id).includes(f.tag)))
      && (!q || ouKey([t.title, t.group, assigneeName(t), t.desc, ...tgOf(tg, t.id).map(id => (tgById(tg, id) as UserTag).name)].join(' ')).includes(q)))
    .sort((a, b) => TK_PRI.findIndex(x => x[0] === (a.priority || 'normal')) - TK_PRI.findIndex(x => x[0] === (b.priority || 'normal')) || (a.due || '9999').localeCompare(b.due || '9999') || (a.createdAt || '').localeCompare(b.createdAt || ''));
}
